<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import {
  useFunctionGetter,
  useMapGetter,
  useStore,
} from 'dashboard/composables/store';
import VoiceNumbersAPI from 'dashboard/api/voiceNumbers';
import { parseContactPhoneNumber } from 'shared/helpers/phoneNumberHelper';
import Input from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SectionLayout from '../account/components/SectionLayout.vue';

// The account's Twilio credentials and the lines that run on them. A line is set up on its inbox's
// Voice tab; this page is where the account itself is connected, its token rotated, and where it
// shows what would stop working without it.
const APP_ID = 'voice_twilio';

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();

const integration = useFunctionGetter('integrations/getIntegration', APP_ID);
const uiFlags = useMapGetter('integrations/getUIFlags');

const lines = ref([]);
const isLoading = ref(true);
const isEditingToken = ref(false);
const newToken = ref('');
const connectForm = reactive({ accountSid: '', authToken: '' });
const disconnectDialogRef = ref(null);

const hook = computed(() => integration.value.hooks?.[0]);
const accountSid = computed(() => hook.value?.settings?.account_sid);

const formatPhone = phone =>
  parseContactPhoneNumber(phone)?.formatInternational() || phone;

const inboxRoute = line => ({
  name: 'settings_inbox_show',
  params: {
    accountId: route.params.accountId,
    inboxId: line.fallback_inbox_id,
    tab: 'voice-configuration',
  },
});

const newLineRoute = computed(() => ({
  name: 'settings_inboxes_page_channel',
  params: { accountId: route.params.accountId, sub_page: 'voice' },
}));

const errorMessage = error =>
  error?.response?.data?.message ||
  t('INTEGRATION_SETTINGS.VOICE_TWILIO.ACCOUNT.ERROR');

const connect = async () => {
  try {
    await store.dispatch('integrations/createHook', {
      app_id: APP_ID,
      settings: {
        account_sid: connectForm.accountSid.trim(),
        auth_token: connectForm.authToken.trim(),
      },
    });
    connectForm.authToken = '';
    useAlert(t('INTEGRATION_SETTINGS.VOICE_TWILIO.ACCOUNT.CONNECTED_MESSAGE'));
  } catch (error) {
    useAlert(errorMessage(error));
  }
};

const cancelTokenEdit = () => {
  isEditingToken.value = false;
  newToken.value = '';
};

const saveToken = async () => {
  try {
    await store.dispatch('integrations/updateHook', {
      hookId: hook.value.id,
      settings: {
        account_sid: accountSid.value,
        auth_token: newToken.value.trim(),
      },
    });
    cancelTokenEdit();
    useAlert(t('INTEGRATION_SETTINGS.VOICE_TWILIO.ACCOUNT.TOKEN_SAVED'));
  } catch (error) {
    useAlert(errorMessage(error));
  }
};

const disconnect = async () => {
  try {
    await store.dispatch('integrations/deleteHook', {
      appId: APP_ID,
      hookId: hook.value.id,
    });
    disconnectDialogRef.value.close();
    useAlert(t('INTEGRATION_SETTINGS.VOICE_TWILIO.DISCONNECT.DONE'));
  } catch (error) {
    disconnectDialogRef.value.close();
    useAlert(errorMessage(error));
  }
};

onMounted(async () => {
  try {
    const [, response] = await Promise.all([
      store.dispatch('integrations/get'),
      VoiceNumbersAPI.get(),
    ]);
    lines.value = response.data.payload;
  } catch {
    useAlert(t('INTEGRATION_SETTINGS.VOICE_TWILIO.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
});
</script>

<template>
  <SettingsLayout :is-loading="isLoading">
    <template #header>
      <BaseSettingsHeader
        :title="integration.name || ''"
        :description="t('INTEGRATION_SETTINGS.VOICE_TWILIO.DESCRIPTION')"
        :back-button-label="t('INTEGRATION_SETTINGS.HEADER')"
      />
    </template>
    <template #body>
      <div class="flex flex-col gap-1">
        <SectionLayout
          :title="t('INTEGRATION_SETTINGS.VOICE_TWILIO.ACCOUNT.TITLE')"
          :description="
            t('INTEGRATION_SETTINGS.VOICE_TWILIO.ACCOUNT.DESCRIPTION')
          "
        >
          <form
            v-if="!hook"
            class="flex flex-col gap-4 p-5 rounded-xl bg-n-solid-1 outline outline-1 outline-n-weak -outline-offset-1"
            @submit.prevent="connect"
          >
            <Input
              v-model="connectForm.accountSid"
              :label="
                t('INTEGRATION_SETTINGS.VOICE_TWILIO.ACCOUNT.ACCOUNT_SID')
              "
              placeholder="AC..."
            />
            <Input
              v-model="connectForm.authToken"
              type="password"
              :label="t('INTEGRATION_SETTINGS.VOICE_TWILIO.ACCOUNT.AUTH_TOKEN')"
            />
            <div>
              <NextButton
                type="submit"
                :label="t('INTEGRATION_SETTINGS.VOICE_TWILIO.ACCOUNT.CONNECT')"
                :is-loading="uiFlags.isCreatingHook"
                :disabled="!connectForm.accountSid || !connectForm.authToken"
              />
            </div>
          </form>

          <div
            v-else
            class="flex flex-col divide-y divide-n-weak rounded-xl bg-n-solid-1 outline outline-1 outline-n-weak -outline-offset-1"
          >
            <div class="flex flex-wrap items-center gap-x-4 gap-y-1 px-5 py-4">
              <span class="w-32 text-sm text-n-slate-11">
                {{ t('INTEGRATION_SETTINGS.VOICE_TWILIO.ACCOUNT.ACCOUNT_SID') }}
              </span>
              <span class="flex-1 font-mono text-sm text-n-slate-12 break-all">
                {{ accountSid }}
              </span>
              <span
                class="flex items-center gap-1.5 text-sm font-medium text-n-teal-11"
              >
                <span class="rounded-full size-2 bg-n-teal-9" />
                {{ t('INTEGRATION_SETTINGS.VOICE_TWILIO.ACCOUNT.CONNECTED') }}
              </span>
            </div>
            <div class="flex flex-col gap-3 px-5 py-4">
              <div class="flex flex-wrap items-center gap-x-4 gap-y-1">
                <span class="w-32 text-sm text-n-slate-11">
                  {{
                    t('INTEGRATION_SETTINGS.VOICE_TWILIO.ACCOUNT.AUTH_TOKEN')
                  }}
                </span>
                <span class="flex-1 text-sm text-n-slate-11">
                  {{
                    t(
                      'INTEGRATION_SETTINGS.VOICE_TWILIO.ACCOUNT.AUTH_TOKEN_HIDDEN'
                    )
                  }}
                </span>
                <NextButton
                  v-if="!isEditingToken"
                  type="button"
                  faded
                  slate
                  sm
                  icon="i-lucide-key-round"
                  :label="
                    t('INTEGRATION_SETTINGS.VOICE_TWILIO.ACCOUNT.CHANGE_TOKEN')
                  "
                  @click="isEditingToken = true"
                />
              </div>
              <form
                v-if="isEditingToken"
                class="flex flex-col gap-3"
                @submit.prevent="saveToken"
              >
                <Input
                  v-model="newToken"
                  type="password"
                  autofocus
                  :label="
                    t('INTEGRATION_SETTINGS.VOICE_TWILIO.ACCOUNT.NEW_TOKEN')
                  "
                />
                <p class="m-0 text-sm text-n-slate-11">
                  {{
                    t(
                      'INTEGRATION_SETTINGS.VOICE_TWILIO.ACCOUNT.NEW_TOKEN_HELP'
                    )
                  }}
                </p>
                <div class="flex gap-2">
                  <NextButton
                    type="submit"
                    :label="
                      t('INTEGRATION_SETTINGS.VOICE_TWILIO.ACCOUNT.SAVE_TOKEN')
                    "
                    :is-loading="uiFlags.isUpdatingHook"
                    :disabled="!newToken.trim()"
                  />
                  <NextButton
                    type="button"
                    faded
                    slate
                    :label="
                      t('INTEGRATION_SETTINGS.VOICE_TWILIO.ACCOUNT.CANCEL')
                    "
                    @click="cancelTokenEdit"
                  />
                </div>
              </form>
            </div>
          </div>
        </SectionLayout>

        <SectionLayout
          :title="t('INTEGRATION_SETTINGS.VOICE_TWILIO.LINES.TITLE')"
          :description="
            t('INTEGRATION_SETTINGS.VOICE_TWILIO.LINES.DESCRIPTION')
          "
          with-border
        >
          <div class="flex flex-col gap-3">
            <ul
              v-if="lines.length"
              class="flex flex-col p-0 m-0 list-none divide-y divide-n-weak rounded-xl bg-n-solid-1 outline outline-1 outline-n-weak -outline-offset-1"
            >
              <li
                v-for="line in lines"
                :key="line.id"
                class="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-4"
              >
                <span
                  class="grid flex-shrink-0 rounded-full size-9 place-content-center bg-n-alpha-2 text-n-slate-11"
                >
                  <Icon icon="i-lucide-phone" class="size-4" />
                </span>
                <div class="flex flex-col flex-1 min-w-0 gap-0.5">
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ formatPhone(line.phone_number) }}
                  </span>
                  <span class="text-sm truncate text-n-slate-11">
                    {{
                      t('INTEGRATION_SETTINGS.VOICE_TWILIO.LINES.INBOX', {
                        name: line.fallback_inbox_name,
                      })
                    }}
                  </span>
                </div>
                <span
                  class="px-2 py-0.5 text-xs rounded-md"
                  :class="
                    line.inbound_enabled
                      ? 'bg-n-teal-3 text-n-teal-11'
                      : 'bg-n-alpha-2 text-n-slate-11'
                  "
                >
                  {{
                    line.inbound_enabled
                      ? t('INTEGRATION_SETTINGS.VOICE_TWILIO.LINES.INBOUND_ON')
                      : t('INTEGRATION_SETTINGS.VOICE_TWILIO.LINES.INBOUND_OFF')
                  }}
                </span>
                <NextButton
                  type="button"
                  faded
                  slate
                  sm
                  trailing-icon
                  icon="i-lucide-chevron-right"
                  :label="t('INTEGRATION_SETTINGS.VOICE_TWILIO.LINES.OPEN')"
                  @click="router.push(inboxRoute(line))"
                />
              </li>
            </ul>
            <p v-else class="m-0 text-sm text-n-slate-11">
              {{ t('INTEGRATION_SETTINGS.VOICE_TWILIO.LINES.EMPTY') }}
            </p>
            <div>
              <NextButton
                type="button"
                faded
                slate
                icon="i-lucide-plus"
                :label="t('INTEGRATION_SETTINGS.VOICE_TWILIO.LINES.ADD')"
                @click="router.push(newLineRoute)"
              />
            </div>
          </div>
        </SectionLayout>

        <SectionLayout
          v-if="hook"
          :title="t('INTEGRATION_SETTINGS.VOICE_TWILIO.DISCONNECT.TITLE')"
          :description="
            lines.length
              ? t(
                  'INTEGRATION_SETTINGS.VOICE_TWILIO.DISCONNECT.BLOCKED',
                  { count: lines.length },
                  lines.length
                )
              : t('INTEGRATION_SETTINGS.VOICE_TWILIO.DISCONNECT.DESCRIPTION')
          "
          with-border
        >
          <div>
            <NextButton
              type="button"
              faded
              ruby
              icon="i-lucide-unplug"
              :disabled="lines.length > 0"
              :label="t('INTEGRATION_SETTINGS.VOICE_TWILIO.DISCONNECT.BUTTON')"
              @click="disconnectDialogRef.open()"
            />
          </div>
        </SectionLayout>
      </div>

      <Dialog
        ref="disconnectDialogRef"
        type="alert"
        :title="t('INTEGRATION_SETTINGS.VOICE_TWILIO.DISCONNECT.CONFIRM_TITLE')"
        :description="
          t('INTEGRATION_SETTINGS.VOICE_TWILIO.DISCONNECT.CONFIRM_MESSAGE')
        "
        :confirm-button-label="
          t('INTEGRATION_SETTINGS.VOICE_TWILIO.DISCONNECT.BUTTON')
        "
        :is-loading="uiFlags.isDeletingHook"
        @confirm="disconnect"
      />
    </template>
  </SettingsLayout>
</template>
